import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * Temporarily removes cross-origin iframes from the DOM during a callback,
 * then immediately restores them. Prevents GSAP ScrollTrigger from throwing
 * SecurityError when it encounters cross-origin iframes during initialization
 * (e.g. Netlify Drawer, Contentful preview).
 */
export function withSafeCrossOriginIframes<T>(fn: () => T): T {
    const removed: { iframe: HTMLIFrameElement; parent: Node; next: Node | null }[] = [];

    document.querySelectorAll('iframe').forEach((iframe) => {
        try {
            void iframe.contentWindow?.document;
        } catch {
            if (iframe.parentNode) {
                removed.push({ iframe, parent: iframe.parentNode, next: iframe.nextSibling });
                iframe.parentNode.removeChild(iframe);
            }
        }
    });

    try {
        return fn();
    } finally {
        removed.forEach(({ iframe, parent, next }) => {
            parent.insertBefore(iframe, next);
        });
    }
}

let _refreshPatched = false;

/**
 * Patches ScrollTrigger.refresh so cross-origin iframes are temporarily
 * removed during every refresh cycle (not just during initial setup).
 * This handles iframes injected after component mount (e.g. Netlify toolbar
 * on first page load). Safe to call multiple times — only patches once.
 */
export function patchScrollTriggerRefresh() {
    if (_refreshPatched) return;
    _refreshPatched = true;

    const origRefresh = ScrollTrigger.refresh;
    ScrollTrigger.refresh = function (safe?: boolean) {
        return withSafeCrossOriginIframes(() => origRefresh.call(ScrollTrigger, safe));
    };
}
